/**
 * Documentation registry + navigation helpers (ADR-0074).
 *
 * The single source of truth for the docs sidebar, the route resolver, the
 * prev/next pager, and the per-locale route expansion in `site-routes.ts`.
 * Sections are added here as each documentation unit lands.
 */
import { AUTH_SECTION } from './auth';
import { BILLING_SECTION } from './billing';
import { CLI_SECTION } from './cli';
import { CORE_CONCEPTS_SECTION } from './core-concepts';
import { DASHBOARD_SECTION } from './dashboard';
import { FRAMEWORK_GUIDES_SECTION } from './framework-guides';
import { GETTING_STARTED_SECTION } from './getting-started';
import { GUIDES_SECTION } from './guides';
import { INTRODUCTION_SECTION } from './introduction';
import { MCP_SECTION } from './mcp';
import { REFERENCE_SECTION } from './reference';
import { SDKS_SECTION } from './sdks';
import { TRUST_SECTION } from './trust';
import { WIDGET_SECTION } from './widget';

import type { DocPageContent, DocSection } from './types';

/**
 * Ordered sections, in sidebar display order.
 *
 * The three product surfaces — dashboard, widget, MCP server — come before the
 * CLI, because that is the order a customer meets them in: the widget goes on
 * the page and discovery runs from there, and the CLI is the optional deeper
 * pass over source code for a team that wants exact argument schemas. It used
 * to sit fourth, directly after Core concepts, which put a tool most readers
 * never run ahead of the three things all of them use.
 *
 * Framework guides travel with the CLI: each one is about what a scan reads out
 * of that framework's source.
 */
export const DOC_SECTIONS: readonly DocSection[] = [
  INTRODUCTION_SECTION,
  GETTING_STARTED_SECTION,
  CORE_CONCEPTS_SECTION,
  DASHBOARD_SECTION,
  WIDGET_SECTION,
  MCP_SECTION,
  CLI_SECTION,
  FRAMEWORK_GUIDES_SECTION,
  AUTH_SECTION,
  SDKS_SECTION,
  BILLING_SECTION,
  TRUST_SECTION,
  GUIDES_SECTION,
  REFERENCE_SECTION,
];

/** Every page, flattened in sidebar order (drives prev/next + route expansion). */
export const DOC_PAGES: readonly DocPageContent[] = DOC_SECTIONS.flatMap((s) => s.pages);

/** Every page slug, in order. */
export const DOC_SLUGS: readonly string[] = DOC_PAGES.map((p) => p.slug);

/** The first page — the canonical destination for the bare `/docs` index CTA. */
export const FIRST_DOC_SLUG: string = DOC_SLUGS[0] ?? 'introduction';

const PAGE_BY_SLUG: ReadonlyMap<string, DocPageContent> = new Map(
  DOC_PAGES.map((p) => [p.slug, p]),
);

/** Resolve a page by slug, or `undefined` for an unknown slug (→ 404). */
export function getDocPage(slug: string): DocPageContent | undefined {
  return PAGE_BY_SLUG.get(slug);
}

/** Resolve the section a page belongs to. */
export function getSectionForSlug(slug: string): DocSection | undefined {
  return DOC_SECTIONS.find((s) => s.pages.some((p) => p.slug === slug));
}

export interface DocNeighbors {
  readonly prev: DocPageContent | undefined;
  readonly next: DocPageContent | undefined;
}

/** Previous/next pages around `slug` in flattened order. */
export function getDocNeighbors(slug: string): DocNeighbors {
  const i = DOC_PAGES.findIndex((p) => p.slug === slug);
  if (i === -1) return { prev: undefined, next: undefined };
  return {
    prev: i > 0 ? DOC_PAGES[i - 1] : undefined,
    next: i < DOC_PAGES.length - 1 ? DOC_PAGES[i + 1] : undefined,
  };
}
